import React, { useState } from 'react';
import { Box, Heading, HStack, VStack, Button, Input, Textarea, Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, IconButton, Text, Select, Badge, Flex, Tag, CloseButton, Alert, AlertIcon, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { FiPlus, FiTrash2, FiX, FiPlay, FiEdit2 } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const SettingsFormulaRules = () => {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [q, setQ] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ template_id: '', target_field: '', formula_expression: '', execution_order: 0 });
  const [testFormula, setTestFormula] = useState('');
  const [testData, setTestData] = useState('{\n  "qty": 5,\n  "unit_price": 100,\n  "margin": 15,\n  "usage1_ratio": 1.05,\n  "usage2_ratio": 1.10\n}');
  const [testResult, setTestResult] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ruleId: null, ruleName: '' });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  const { data: rules } = useQuery(['formula_rules', q, selectedTemplateId, page, pageSize], () => {
    if (!selectedTemplateId) {
      return Promise.resolve([]);
    }
    return api.get(`/api/formula-rules/template/${selectedTemplateId}`).then(res => res.data.data.rules || []);
  });
  
  const { data: templates } = useQuery('proposal_templates', () => 
    api.get('/api/settings/templates').then(res => res.data.data.templates)
  );

  const createRule = useMutation((data) => api.post('/api/formula-rules', data), { 
    onSuccess: () => { 
      queryClient.invalidateQueries(['formula_rules', q]); 
      setIsOpen(false); 
      setForm({ template_id: '', target_field: '', formula_expression: '', execution_order: 0 }); 
      toast({ title: t('common.success'), status: 'success' }); 
    }
  });
  
  const updateRule = useMutation(({ id, data }) => api.put(`/api/formula-rules/${id}`, data), { 
    onSuccess: () => { 
      queryClient.invalidateQueries(['formula_rules', q]); 
      setIsOpen(false); 
      setEditing(null); 
      toast({ title: t('common.success'), status: 'success' }); 
    }
  });
  
  const deleteRule = useMutation((id) => api.delete(`/api/formula-rules/${id}`), { 
    onSuccess: () => { 
      queryClient.invalidateQueries(['formula_rules', q]); 
      toast({ title: t('common.success'), status: 'success' }); 
    }
  });

  const testFormulaMutation = useMutation((data) => api.post('/api/formula-rules/test', data), {
    onSuccess: (response) => {
      setTestResult(response.data.data.result);
      toast({ title: 'Formula test successful', status: 'success' });
    },
    onError: (error) => {
      setTestResult(null);
      toast({ title: 'Formula test failed', description: error.response?.data?.error, status: 'error' });
    }
  });

  const openCreate = () => { 
    setEditing(null); 
    setForm({ template_id: '', target_field: '', formula_expression: '', execution_order: 0 }); 
    setIsOpen(true); 
  };
  
  const openEdit = (rule) => { 
    setEditing(rule); 
    setForm({ 
      template_id: rule.template_id, 
      target_field: rule.target_field, 
      formula_expression: rule.formula_expression, 
      execution_order: rule.execution_order 
    }); 
    setIsOpen(true); 
  };

  // Handle template change with warning for editing rules
  const handleTemplateChange = (templateId) => {
    if (editing && form.template_id !== templateId) {
      // Warning: Template changed, rule will become invalid
      toast({ 
        title: t('common.warning') || 'Warning', 
        description: 'Template changed. This rule will become invalid.', 
        status: 'warning',
        duration: 5000,
        isClosable: true
      });
    }
    setForm({ ...form, template_id: templateId });
  };

  const saveRule = () => {
    if (!form.template_id || !form.target_field || !form.formula_expression) { 
      toast({ title: t('common.error'), description: 'All fields are required', status: 'error' }); 
      return; 
    }
    
    if (editing) updateRule.mutate({ id: editing.id, data: form }); 
    else createRule.mutate(form);
  };

  // Helper function to get template usages dynamically
  const getTemplateUsages = (templateId) => {
    if (!templateId || !templates) return {};
    const template = templates.find(t => t.id === parseInt(templateId));
    return template?.json?.usages || {};
  };

  const handleTestFormula = () => {
    if (!testFormula.trim()) {
      toast({ title: 'Error', description: 'Please enter a formula to test', status: 'error' });
      return;
    }

    let parsedTestData;
    try {
      parsedTestData = JSON.parse(testData);
    } catch (e) {
      toast({ title: 'Error', description: 'Invalid JSON test data', status: 'error' });
      return;
    }

    testFormulaMutation.mutate({
      formula_expression: testFormula,
      test_data: parsedTestData
    });
  };

  const getTemplateName = (templateId) => {
    const template = templates?.find(t => t.id === templateId);
    return template?.name || `Template ${templateId}`;
  };

  const getFieldDisplayName = (field) => {
    const fieldMap = {
      'table.qty': 'Quantity',
      'table.unit_price': 'Unit Price',
      'table.slt': 'Sale Line Total',
      'table.final_price': 'Final Price',
      'table.margin': 'Margin',
      'table.unit_price_x_amount': 'Unit Price × Amount'
    };

    if (field.startsWith('template.usage')) {
      const parts = field.split('.');
      if (parts.length >= 3) {
        return `${parts[1]} ${parts[2]}`;
      }
    }

    return fieldMap[field] || field;
  };

  // Copy default template rules to selected template
  const copyDefaultTemplateRules = async (targetTemplateId) => {
    try {
      // Get default template rules (ID: 1)
      const defaultRulesResponse = await api.get('/api/formula-rules/template/1');
      const defaultRules = defaultRulesResponse.data.data.rules || [];
      
      if (defaultRules.length === 0) {
        toast({ 
          title: t('common.warning') || 'Warning', 
          description: 'No default rules found to copy', 
          status: 'warning' 
        });
        return;
      }

      // Copy each rule to target template
      const copyPromises = defaultRules.map(rule => 
        api.post('/api/formula-rules', {
          template_id: parseInt(targetTemplateId),
          target_field: rule.target_field,
          formula_expression: rule.formula_expression,
          execution_order: rule.execution_order
        })
      );

      await Promise.all(copyPromises);
      
      toast({ 
        title: t('common.success'), 
        description: `Copied ${defaultRules.length} rules from default template`, 
        status: 'success' 
      });
      
      // Refresh the rules list
      queryClient.invalidateQueries(['formula_rules', q]);
      
    } catch (error) {
      console.error('Failed to copy default template rules:', error);
      toast({ 
        title: t('common.error'), 
        description: 'Failed to copy default template rules', 
        status: 'error' 
      });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('formula.rules_title') || 'Formula Rules'}</Heading>
        <HStack>
          <Input placeholder={t('common.search') || 'Search'} value={q} onChange={(e) => setQ(e.target.value)} maxW="250px" />
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>{t('formula.new_rule') || 'New Rule'}</Button>
        </HStack>
      </HStack>

      {/* Template Selection */}
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="semibold">
                {t('common.template') || 'Template'}
                {selectedTemplateId && rules && (
                  <Badge ml={2} colorScheme="blue" variant="subtle">
                    {rules.length} rules
                  </Badge>
                )}
              </Text>
              <Button 
                size="sm" 
                variant="outline" 
                colorScheme="blue"
                onClick={() => {
                  if (selectedTemplateId && selectedTemplateId !== '1') {
                    copyDefaultTemplateRules(selectedTemplateId);
                  }
                }}
                isDisabled={!selectedTemplateId || selectedTemplateId === '1'}
              >
                {t('formula.copy_default_rules') || 'Copy Default Template Rules'}
              </Button>
            </HStack>
            <Select 
              placeholder={t('formula.select_template_to_view_rules') || 'Select template to view rules'} 
              value={selectedTemplateId} 
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                // Auto-set template_id in form when creating new rule
                if (!editing) {
                  setForm(prev => ({ ...prev, template_id: e.target.value }));
                }
              }}
            >
              {templates?.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.id === 1 ? '(Default)' : ''}
                </option>
              ))}
            </Select>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {!selectedTemplateId ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="lg">
                {t('formula.select_template_to_view_rules') || 'Please select a template to view formula rules'}
              </Text>
            </Box>
          ) : rules && rules.length === 0 ? (
            <Box textAlign="center" py={8}>
              <VStack spacing={4}>
                <Text color="gray.500" fontSize="lg">
                  No formula rules found for this template
                </Text>
                {selectedTemplateId !== '1' && (
                  <Button 
                    size="sm" 
                    colorScheme="blue"
                    onClick={() => copyDefaultTemplateRules(selectedTemplateId)}
                  >
                    Copy Default Template Rules
                  </Button>
                )}
              </VStack>
            </Box>
          ) : (
            <>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('common.id') || 'ID'}</Th>
                    <Th>{t('common.template') || 'Template'}</Th>
                    <Th>{t('formula.target_field') || 'Target Field'}</Th>
                    <Th>{t('formula.expression') || 'Formula'}</Th>
                    <Th>{t('formula.execution_order') || 'Order'}</Th>
                    <Th>{t('common.status') || 'Status'}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rules?.map(rule => (
                <Tr key={rule.id}>
                  <Td>{rule.id}</Td>
                  <Td>{getTemplateName(rule.template_id)}</Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle">
                      {getFieldDisplayName(rule.target_field)}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="xs" fontFamily="mono" maxW="300px" noOfLines={2}>
                      {rule.formula_expression}
                    </Text>
                  </Td>
                  <Td>{rule.execution_order}</Td>
                  <Td>
                    <Badge colorScheme={rule.is_active ? 'green' : 'red'} variant="subtle">
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack>
                      <Button size="xs" variant="outline" onClick={() => openEdit(rule)} leftIcon={<FiEdit2 />}>
                        {t('common.edit') || 'Edit'}
                      </Button>
                      <IconButton 
                        aria-label={t('common.delete') || 'delete'} 
                        size="xs" 
                        colorScheme="red" 
                        icon={<FiTrash2 />} 
                        onClick={() => {
                          if (window.confirm(t('messages.confirm_delete') || 'Are you sure you want to delete this formula rule?')) {
                            deleteRule.mutate(rule.id);
                          }
                        }} 
                        isDisabled={rule.template_id === 1}
                        title={rule.template_id === 1 ? 'Default template rules cannot be deleted' : ''}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
                </Tbody>
              </Table>
            </>
          )}
        </CardBody>
      </Card>

      {/* Formula Usage Legend */}
      <Card mt={6}>
        <CardBody>
          <Heading size="md" mb={4}>📚 Formula Usage Guide</Heading>
          
          <VStack spacing={6} align="stretch">
            {/* Available Variables */}
            <Box>
              <Heading size="sm" mb={3} color="blue.600">🔧 Available Variables</Heading>
              <VStack spacing={3} align="stretch">
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('formula.table_fields') || 'Table Fields:'}</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.qty</Badge>
                      <Text fontSize="sm">- {t('table.qty') || 'Quantity'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.unit_price</Badge>
                      <Text fontSize="sm">- {t('table.unit_price') || 'Unit Price'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.margin</Badge>
                      <Text fontSize="sm">- {t('table.margin') || 'Margin'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.slt</Badge>
                      <Text fontSize="sm">- {t('table.slt') || 'Sale Line Total'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.sppu</Badge>
                      <Text fontSize="sm">- {t('table.sppu') || 'Sale Price Per Unit'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.final_price</Badge>
                      <Text fontSize="sm">- {t('table.final_price') || 'Final Price'}</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" minW="120px">table.unit_price_x_amount</Badge>
                      <Text fontSize="sm">- {t('table.unit_price_x_amount') || 'Unit Price × Amount'}</Text>
                    </HStack>
                  </VStack>
                </Box>
                
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('formula.template_usage') || 'Template Usage Fields:'}</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <HStack>
                      <Badge colorScheme="green" variant="subtle" minW="120px">template.usage1.ratio</Badge>
                      <Text fontSize="sm">- Usage 1 multiplier ratio</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="green" variant="subtle" minW="120px">template.usage2.ratio</Badge>
                      <Text fontSize="sm">- Usage 2 multiplier ratio</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="green" variant="subtle" minW="120px">template.usage1.rowResult</Badge>
                      <Text fontSize="sm">- Calculated result for usage 1</Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Available Functions */}
            <Box>
              <Heading size="sm" mb={3} color="green.600">⚡ Available Functions</Heading>
              <VStack spacing={2} align="stretch" pl={4}>
                <HStack>
                  <Badge colorScheme="green" variant="subtle" minW="80px">ROUND()</Badge>
                                        <Text fontSize="sm">- Round to specified decimal places</Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="green" variant="subtle" minW="80px">MAX()</Badge>
                                        <Text fontSize="sm">- Maximum value from multiple values</Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="green" variant="subtle" minW="80px">MIN()</Badge>
                                        <Text fontSize="sm">- Minimum value from multiple values</Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="green" variant="subtle" minW="80px">IF()</Badge>
                  <Text fontSize="sm">- Conditional logic: IF(condition, true_value, false_value)</Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="green" variant="subtle" minW="80px">ABS()</Badge>
                                        <Text fontSize="sm">- Absolute value</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Example Formulas */}
            <Box>
              <Heading size="sm" mb={3} color="purple.600">💡 Example Formulas</Heading>
              <VStack spacing={3} align="stretch" pl={4}>
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('formula.basic_calculations') || 'Basic Calculations:'}</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">SLT (Sale Line Total):</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        table.qty * table.unit_price * (1 + table.margin/100)
                      </Text>
                      <Text fontSize="xs" color="gray.600">Quantity × Unit Price × (1 + Margin/100)</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">SPPU (Sale Price Per Unit):</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        table.unit_price * (1 + table.margin/100)
                      </Text>
                      <Text fontSize="xs" color="gray.600">Unit Price × (1 + Margin/100)</Text>
                    </Box>
                  </VStack>
                </Box>
                
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('formula.usage_calculations') || 'Usage Calculations:'}</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">Usage 1 Result:</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        table.slt * template.usage1.ratio
                      </Text>
                      <Text fontSize="xs" color="gray.600">SLT × Usage 1 ratio</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">Usage 2 Result:</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        table.slt * template.usage2.ratio
                      </Text>
                      <Text fontSize="xs" color="gray.600">SLT × Usage 2 ratio</Text>
                    </Box>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('formula.advanced_examples') || 'Advanced Examples:'}</Text>
                  <VStack spacing={2} align="stretch" pl={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">With KDV (VAT):</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        table.slt * (1 + 0.18)
                      </Text>
                      <Text fontSize="xs" color="gray.600">SLT × (1 + 18% VAT)</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="purple.600">Conditional Margin:</Text>
                      <Text fontSize="xs" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
                        IF(table.qty > 10, 0.15, 0.10)
                      </Text>
                      <Text fontSize="xs" color="gray.600">If quantity > 10 then 15%, else 10% margin</Text>
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Important Notes */}
            <Box>
              <Heading size="sm" mb={3} color="orange.600">⚠️ Important Notes</Heading>
              <VStack spacing={2} align="stretch" pl={4}>
                <Text fontSize="sm">• Formulas are executed in order based on <Badge colorScheme="orange">execution_order</Badge></Text>
                <Text fontSize="sm">• Use <Badge colorScheme="blue">table.*</Badge> for item-level calculations</Text>
                <Text fontSize="sm">• Use <Badge colorScheme="green">template.usage*.ratio</Badge> for usage multipliers</Text>
                <Text fontSize="sm">• All calculations support standard math operators: +, -, *, /, (), %</Text>
                <Text fontSize="sm">• Test your formulas using the Formula Test Panel below</Text>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Formula Test Panel */}
      <Card mt={6}>
        <CardBody>
          <Heading size="md" mb={4}>{t('formula.test_panel') || 'Formula Test Panel'}</Heading>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Formula Expression</FormLabel>
              <Textarea 
                value={testFormula}
                onChange={(e) => setTestFormula(e.target.value)}
                placeholder="Enter formula to test (e.g., table.qty * table.unit_price * (1 + table.margin/100))"
                fontFamily="mono"
                minH="80px"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Test Data (JSON)</FormLabel>
              <Textarea 
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                placeholder="Enter test data as JSON"
                fontFamily="mono"
                minH="120px"
              />
            </FormControl>
            
            <HStack>
              <Button onClick={handleTestFormula} isLoading={testFormulaMutation.isLoading} leftIcon={<FiPlay />}>
                Test Formula
              </Button>
              {testResult !== null && (
                <Alert status="success">
                  <AlertIcon />
                  Result: {testResult}
                </Alert>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Create/Edit Rule Modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? (t('formula.edit_rule') || 'Edit Formula Rule') : (t('formula.new_rule') || 'New Formula Rule')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>{t('common.template') || 'Template'}</FormLabel>
                <Select 
                  value={form.template_id} 
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  placeholder={t('common.select') || 'Select template'}
                >
                  {templates?.map(tpl => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>{t('formula.target_field') || 'Target Field'}</FormLabel>
                <Select 
                  value={form.target_field} 
                  onChange={(e) => setForm({ ...form, target_field: e.target.value })}
                  placeholder={t('common.select') || 'Select target field'}
                >
                  <option value="table.qty">{t('table.qty') || 'Quantity'}</option>
                  <option value="table.unit_price">{t('table.unit_price') || 'Unit Price'}</option>
                  <option value="table.slt">{t('table.slt') || 'Sale Line Total'}</option>
                  <option value="table.sppu">{t('table.sppu') || 'Sale Price Per Unit'}</option>
                  <option value="table.final_price">{t('table.final_price') || 'Final Price'}</option>
                  <option value="table.margin">{t('table.margin') || 'Margin'}</option>
                  <option value="table.unit_price_x_amount">{t('table.unit_price_x_amount') || 'Unit Price × Amount'}</option>
                  {/* Dynamic usage options based on selected template */}
                  {Object.keys(getTemplateUsages(form.template_id)).map(usageKey => {
                    const usage = getTemplateUsages(form.template_id)[usageKey];
                    const translation = usage?.[`lang.${currentLanguage}`];
                    
                    return (
                      <option key={usageKey} value={`template.${usageKey}.rowResult`}>
                        {translation || usageKey}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>{t('formula.expression') || 'Formula Expression'}</FormLabel>
                <Textarea 
                  value={form.formula_expression}
                  onChange={(e) => setForm({ ...form, formula_expression: e.target.value })}
                  placeholder={t('formula.placeholder') || 'Enter formula (e.g., table.qty * table.unit_price * (1 + table.margin/100))'}
                  fontFamily="mono"
                  minH="100px"
                />
                
                {/* Clickable Tag List */}
                <Box mt={3}>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                    {t('formula.click_to_add') || 'Click to add variables:'}
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {/* Table Fields */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="blue.600" mb={1}>
                        {t('formula.table_fields') || 'Table Fields:'}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.qty' 
                          }))}
                        >
                          {t('table.qty') || 'Qty'} → table.qty
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.unit_price' 
                          }))}
                        >
                          {t('table.unit_price') || 'UP'} → table.unit_price
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.margin' 
                          }))}
                        >
                          {t('table.margin') || 'Margin'} → table.margin
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.slt' 
                          }))}
                        >
                          {t('table.slt') || 'SLT'} → table.slt
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.sppu' 
                          }))}
                        >
                          {t('table.sppu') || 'SPPU'} → table.sppu
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.final_price' 
                          }))}
                        >
                          {t('table.final_price') || 'FP'} → table.final_price
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="blue" 
                          variant="outline"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + 'table.unit_price_x_amount' 
                          }))}
                        >
                          {t('table.unit_price_x_amount') || 'UP×Qty'} → table.unit_price_x_amount
                        </Badge>
                      </HStack>
                    </Box>
                    
                    {/* Template Usage Fields */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="green.600" mb={1}>
                        {t('formula.template_usage') || 'Template Usage:'}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {/* Dynamic usage ratio tags based on selected template */}
                        {Object.keys(getTemplateUsages(form.template_id)).map(usageKey => (
                          <Badge 
                            key={usageKey}
                            cursor="pointer" 
                            colorScheme="green" 
                            variant="outline"
                            _hover={{ bg: 'green.50' }}
                            onClick={() => setForm(prev => ({ 
                              ...prev, 
                              formula_expression: prev.formula_expression + `template.${usageKey}.ratio` 
                            }))}
                          >
                            {getTemplateUsages(form.template_id)[usageKey]?.[`lang.${currentLanguage}`] || usageKey} → template.{usageKey}.ratio
                          </Badge>
                        ))}
                      </HStack>
                      
                      {/* Usage Result Fields */}
                      <HStack spacing={2} flexWrap="wrap" mt={2}>
                        {/* Dynamic usage result field tags based on selected template */}
                        {Object.keys(getTemplateUsages(form.template_id)).map(usageKey => (
                          <Badge 
                            key={`result-${usageKey}`}
                            cursor="pointer" 
                            colorScheme="teal" 
                            variant="outline"
                            _hover={{ bg: 'teal.50' }}
                            onClick={() => setForm(prev => ({ 
                              ...prev, 
                              formula_expression: prev.formula_expression + `template.${usageKey}.rowResult` 
                            }))}
                          >
                            {getTemplateUsages(form.template_id)[usageKey]?.[`lang.${currentLanguage}`] || usageKey} Result → template.{usageKey}.rowResult
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                    
                    {/* Math Operators */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="purple.600" mb={1}>
                        {t('formula.math_operators') || 'Math Operators:'}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' + ' 
                          }))}
                        >
                          +
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' - ' 
                          }))}
                        >
                          -
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' * ' 
                          }))}
                        >
                          ×
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' / ' 
                          }))}
                        >
                          ÷
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' ( ' 
                          }))}
                        >
                          (
                        </Badge>
                        <Badge 
                          cursor="pointer" 
                          colorScheme="purple" 
                          variant="outline"
                          _hover={{ bg: 'purple.50' }}
                          onClick={() => setForm(prev => ({ 
                            ...prev, 
                            formula_expression: prev.formula_expression + ' ) ' 
                          }))}
                        >
                          )
                        </Badge>
                      </HStack>
                    </Box>
                  </VStack>
                </Box>
                
                <Text fontSize="xs" color="gray.600" mt={3}>
                  {t('formula.available_variables') || 'Available variables: table.qty, table.unit_price, table.margin, template.usage1.ratio, etc.'}
                </Text>
              </FormControl>
              
              <FormControl>
                <FormLabel>{t('formula.execution_order') || 'Execution Order'}</FormLabel>
                <Input 
                  type="number" 
                  value={form.execution_order} 
                  onChange={(e) => setForm({ ...form, execution_order: Number(e.target.value) })}
                  placeholder="0"
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  {t('formula.execution_order_help') || 'Lower numbers execute first'}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => setIsOpen(false)}>{t('common.cancel') || 'Cancel'}</Button>
            <Button colorScheme="brand" onClick={saveRule} isLoading={createRule.isLoading || updateRule.isLoading}>
              {t('common.save') || 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SettingsFormulaRules;
